/**
 * 自定义原子类CSS
 * 支持w-[10px], h-[20vh], m-[5px], text-[#ff0000]等动态类名
 * 支持的单位：px, em, rem, %, vh, vw, vmin, vmax等所有CSS单位
 * 支持的颜色格式：#十六进制, rgb/rgba, hsl/hsla, 颜色关键字(如red, blue)
 */ (function() {
    'use strict';
    // 配置参数
    const CONFIG = {
        processed: new Set(),
        sheet: null,
        observer: null,
        // 原子化规则配置 - 支持中括号语法
        propRules: {
            // 尺寸类
            width: {
                regex: /^w-\[(.+)\]$/,
                prop: 'width'
            },
            minWidth: {
                regex: /^min-w-\[(.+)\]$/,
                prop: 'min-width'
            },
            maxWidth: {
                regex: /^max-w-\[(.+)\]$/,
                prop: 'max-width'
            },
            height: {
                regex: /^h-\[(.+)\]$/,
                prop: 'height'
            },
            minHeight: {
                regex: /^min-h-\[(.+)\]$/,
                prop: 'min-height'
            },
            maxHeight: {
                regex: /^max-h-\[(.+)\]$/,
                prop: 'max-height'
            },
            // 间距类
            margin: {
                regex: /^m-\[(.+)\]$/,
                prop: 'margin',
                processor: (v)=>v.replace(/_/g, ' ')
            },
            marginTop: {
                regex: /^mt-\[(.+)\]$/,
                prop: 'margin-top'
            },
            marginRight: {
                regex: /^mr-\[(.+)\]$/,
                prop: 'margin-right'
            },
            marginBottom: {
                regex: /^mb-\[(.+)\]$/,
                prop: 'margin-bottom'
            },
            marginLeft: {
                regex: /^ml-\[(.+)\]$/,
                prop: 'margin-left'
            },
            marginX: {
                regex: /^mx-\[(.+)\]$/,
                multiProps: [
                    'margin-left',
                    'margin-right'
                ]
            },
            marginY: {
                regex: /^my-\[(.+)\]$/,
                multiProps: [
                    'margin-top',
                    'margin-bottom'
                ]
            },
            padding: {
                regex: /^p-\[(.+)\]$/,
                prop: 'padding',
                processor: (v)=>v.replace(/_/g, ' ')
            },
            paddingTop: {
                regex: /^pt-\[(.+)\]$/,
                prop: 'padding-top'
            },
            paddingRight: {
                regex: /^pr-\[(.+)\]$/,
                prop: 'padding-right'
            },
            paddingBottom: {
                regex: /^pb-\[(.+)\]$/,
                prop: 'padding-bottom'
            },
            paddingLeft: {
                regex: /^pl-\[(.+)\]$/,
                prop: 'padding-left'
            },
            paddingX: {
                regex: /^px-\[(.+)\]$/,
                multiProps: [
                    'padding-left',
                    'padding-right'
                ]
            },
            paddingY: {
                regex: /^py-\[(.+)\]$/,
                multiProps: [
                    'padding-top',
                    'padding-bottom'
                ]
            },
            gap: {
                regex: /^gap-\[(.+)\]$/,
                prop: 'gap'
            },
            // 字体和文本类
            fontSize: {
                regex: /^text-\[(.+)\]$/,
                prop: 'font-size'
            },
            lineHeight: {
                regex: /^leading-\[(.+)\]$/,
                prop: 'line-height'
            },
            fontWeight: {
                regex: /^font-\[(.+)\]$/,
                prop: 'font-weight'
            },
            letterSpacing: {
                regex: /^tracking-\[(.+)\]$/,
                prop: 'letter-spacing'
            },
            // 颜色类
            color: {
                regex: /^text-color-\[(.+)\]$/,
                prop: 'color'
            },
            bgColor: {
                regex: /^bg-\[(.+)\]$/,
                prop: 'background-color'
            },
            // 边框类
            border: {
                regex: /^border-\[(.+)\]$/,
                prop: 'border-width',
                postProcessor: (v)=>`${v}; border-style: solid`
            },
            borderTop: {
                regex: /^border-t-\[(.+)\]$/,
                prop: 'border-top-width',
                postProcessor: (v)=>`${v}; border-top-style: solid`
            },
            borderRight: {
                regex: /^border-r-\[(.+)\]$/,
                prop: 'border-right-width',
                postProcessor: (v)=>`${v}; border-right-style: solid`
            },
            borderBottom: {
                regex: /^border-b-\[(.+)\]$/,
                prop: 'border-bottom-width',
                postProcessor: (v)=>`${v}; border-bottom-style: solid`
            },
            borderLeft: {
                regex: /^border-l-\[(.+)\]$/,
                prop: 'border-left-width',
                postProcessor: (v)=>`${v}; border-left-style: solid`
            },
            borderColor: {
                regex: /^border-color-\[(.+)\]$/,
                prop: 'border-color'
            },
            // 圆角类
            borderRadius: {
                regex: /^rounded-\[(.+)\]$/,
                prop: 'border-radius'
            },
            borderRadiusTop: {
                regex: /^rounded-t-\[(.+)\]$/,
                multiProps: [
                    'border-top-left-radius',
                    'border-top-right-radius'
                ]
            },
            borderRadiusRight: {
                regex: /^rounded-r-\[(.+)\]$/,
                multiProps: [
                    'border-top-right-radius',
                    'border-bottom-right-radius'
                ]
            },
            borderRadiusBottom: {
                regex: /^rounded-b-\[(.+)\]$/,
                multiProps: [
                    'border-bottom-left-radius',
                    'border-bottom-right-radius'
                ]
            },
            borderRadiusLeft: {
                regex: /^rounded-l-\[(.+)\]$/,
                multiProps: [
                    'border-top-left-radius',
                    'border-bottom-left-radius'
                ]
            }
        },
        // 响应式断点
        breakpoints: {
            sm: '(min-width: 640px)',
            md: '(min-width: 768px)',
            lg: '(min-width: 1024px)',
            xl: '(min-width: 1280px)'
        }
    };
    /**
   * 验证是否为有效的CSS单位值
   * 支持所有CSS单位：px, em, rem, %, vh, vw, vmin, vmax, pt, pc, in, cm, mm, ex, ch
   * 也支持无单位数值和calc等函数
   * @param {string} value 要验证的值
   * @return {boolean} 是否为有效的值
   */ function isValidCSSValue(value) {
        // 支持所有可能的CSS单位和函数
        return true;
    }
    /**
   * 验证是否为有效的CSS颜色值
   * 支持：颜色关键字(black, white等)、#十六进制、rgb/rgba、hsl/hsla
   * @param {string} color 要验证的颜色
   * @return {boolean} 是否为有效的颜色
   */ function isValidColor(color) {
        // 支持所有合法的CSS颜色值
        return true;
    }
    /* 核心功能实现 */ function init() {
        // 创建样式表
        const styleTag = document.createElement('style');
        styleTag.id = 'atomic-css-styles';
        document.head.appendChild(styleTag);
        CONFIG.sheet = styleTag.sheet;
        // 初始化DOM监听
        CONFIG.observer = new MutationObserver((mutations)=>{
            mutations.forEach((mutation)=>{
                // 处理新增节点
                mutation.addedNodes.forEach((node)=>{
                    if (node.nodeType === 1) {
                        processNode(node);
                        // 处理子节点
                        node.querySelectorAll('*').forEach((child)=>{
                            processNode(child);
                        });
                    }
                });
                // 处理属性变化（类名变化）
                if (mutation.type === 'attributes' && mutation.attributeName === 'class') processNode(mutation.target);
            });
        });
        // 开始监听
        CONFIG.observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: [
                'class'
            ]
        });
        // 处理现有元素
        document.querySelectorAll('*').forEach(processNode);
    }
    function processNode(node) {
        // 跳过无效元素或无类名的元素
        if (!node || !node.className || typeof node.className !== 'string') return;
        const classNames = node.className.split(' ');
        classNames.forEach((className)=>{
            // 过滤空类名和已处理类名
            if (!className || CONFIG.processed.has(className)) return;
            // 处理响应式
            const responsiveMatch = className.match(/^(\w+):([\w\[\]\-\.]+)$/);
            if (responsiveMatch && CONFIG.breakpoints[responsiveMatch[1]]) {
                handleResponsive(responsiveMatch[1], responsiveMatch[2]);
                return;
            }
            // 处理带括号的动态类名
            if (className.includes('[') && className.includes(']')) // 处理普通类名
            Object.entries(CONFIG.propRules).forEach(([key, rule])=>{
                const match = className.match(rule.regex);
                if (match) {
                    generateCSS(className, rule, match[1]);
                    CONFIG.processed.add(className);
                }
            });
        });
    }
    function handleResponsive(screen, baseClass) {
        // 过滤已处理类名
        const responsiveClassName = `${screen}:${baseClass}`;
        if (CONFIG.processed.has(responsiveClassName)) return;
        // 只处理带中括号的动态类名
        if (baseClass.includes('[') && baseClass.includes(']')) Object.entries(CONFIG.propRules).forEach(([key, rule])=>{
            const match = baseClass.match(rule.regex);
            if (match) {
                generateResponsiveCSS(screen, baseClass, rule, match[1]);
                CONFIG.processed.add(responsiveClassName);
            }
        });
    }
    function generateCSS(className, rule, value) {
        // 处理值
        const processedValue = rule.processor ? rule.processor(value) : value;
        // 生成CSS规则
        let cssRules = '';
        if (rule.multiProps) // 多属性规则
        cssRules = `.${escapeClassName(className)} { ${rule.multiProps.map((prop)=>`${prop}: ${processedValue}`).join(' !important; ')} !important; }`;
        else // 单属性规则
        cssRules = `.${escapeClassName(className)} { ${rule.prop}: ${processedValue} !important;${rule.postProcessor ? ' ' + rule.postProcessor(processedValue) : ''} }`;
        safeInsertRule(cssRules);
    }
    function generateResponsiveCSS(screen, baseClass, rule, value) {
        // 处理值
        const processedValue = rule.processor ? rule.processor(value) : value;
        // 生成响应式CSS规则
        let cssContent = '';
        if (rule.multiProps) // 多属性规则
        cssContent = rule.multiProps.map((prop)=>`${prop}: ${processedValue}`).join(' !important; ') + ' !important;';
        else // 单属性规则
        cssContent = `${rule.prop}: ${processedValue} !important;${rule.postProcessor ? ' ' + rule.postProcessor(processedValue) : ''}`;
        const cssRule = `@media ${CONFIG.breakpoints[screen]} { .${escapeClassName(screen + ':' + baseClass)} { ${cssContent} } }`;
        safeInsertRule(cssRule);
    }
    // 安全地插入CSS规则
    function safeInsertRule(rule) {
        try {
            CONFIG.sheet.insertRule(rule, CONFIG.sheet.cssRules.length);
        } catch (e) {
            console.warn("CSS\u89C4\u5219\u63D2\u5165\u5931\u8D25:", e.message, "\n\u89C4\u5219\u5185\u5BB9:", rule);
        }
    }
    // 转义CSS类名中的特殊字符
    function escapeClassName(className) {
        return className.replace(/([[\]])/g, '\\$1').replace(/[:]/g, '\\:').replace(/([#.()+*?^${}|])/g, '\\$1'); // 转义更多CSS选择器特殊字符
    }
    /* 初始化 */ if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
    else init();
})();

//# sourceMappingURL=models.ac54cf77.js.map
